import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { NSButton, NSCard, NSInput } from 'bricks';
import NSDatePicker from 'bricks/NSDatePicker/NSDatePicker';
import { DevelopmentTimelineData, Milestone } from 'views/InvestmentMemo/types';
import { formatDateToYearMonth, parseYearMonthToDate } from 'ns_libs/formatter';
import { useInvestmentMemoContext } from '../../../InvestmentMemoContext';
import './DevelopmentTimelineForm.scss';

export interface DevelopmentTimelineFormProps {
    data: DevelopmentTimelineData;
    onChange: (data: Partial<DevelopmentTimelineData>) => void;
}

const DevelopmentTimelineForm = ({ data, onChange }: DevelopmentTimelineFormProps) => {
    const { shouldShowError, getFieldError } = useInvestmentMemoContext();

    const milestonesByYear = useMemo(() => {
        const grouped: Record<string, Milestone[]> = {};

        data.milestones.forEach(milestone => {
            if (!milestone.date) return;
            const year = milestone.date.substring(0, 4);
            if (!grouped[year]) {
                grouped[year] = [];
            }
            grouped[year].push(milestone);
        });

        // Sort years and milestones within each year
        const sortedYears = Object.keys(grouped).sort();
        const result: { year: string; milestones: Milestone[] }[] = sortedYears.map(year => ({
            year,
            milestones: grouped[year].sort((a, b) => a.date.localeCompare(b.date)),
        }));

        return result;
    }, [data.milestones]);

    const handleAddMilestone = () => {
        const newMilestone: Milestone = {
            id: crypto.randomUUID(),
            date: '',
            name: '',
        };
        onChange({ milestones: [...data.milestones, newMilestone] });
    };

    const handleUpdateMilestone = (id: string, updates: Partial<Milestone>) => {
        const updatedMilestones = data.milestones.map(m => (m.id === id ? { ...m, ...updates } : m));
        onChange({ milestones: updatedMilestones });
    };

    const handleRemoveMilestone = (id: string) => {
        onChange({ milestones: data.milestones.filter(m => m.id !== id) });
    };

    const renderMilestoneRow = (milestone: Milestone) => (
        <div key={milestone.id} className="d-flex align-items-center justify-content-end mb-2 w-100">
            <div>
                <NSDatePicker
                    name={`milestoneDate-${milestone.id}`}
                    value={parseYearMonthToDate(milestone.date)}
                    onChange={(date: Date | null) => handleUpdateMilestone(
                        milestone.id, { date: date ? (formatDateToYearMonth(date.toISOString()) ?? '') : '' },
                    )}
                    showMonthYearPicker
                    dateFormat="MMMM yyyy"
                    placeholderText="Select date"
                    calendarWidth="400px"
                />
            </div>
            <div className="mx-2 w-50">
                <NSInput
                    name={`milestoneName-${milestone.id}`}
                    type="text"
                    value={milestone.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateMilestone(milestone.id, { name: e.target.value })}
                />
            </div>
            <FontAwesomeIcon icon={faXmark} onClick={() => handleRemoveMilestone(milestone.id)} />
        </div>
    );

    const milestonesWithoutDate = data.milestones.filter(m => !m.date);

    return (
        <div className="DevelopmentTimelineForm px-3 pb-3">
            <NSCard className="NSCard--level-3 p-3 mt-4">
                <h5 className="text-white mb-3">Milestones</h5>

                <div>
                    {milestonesByYear.map(({ year, milestones }) => (
                        <div key={year} className="mb-3">
                            <div className="d-flex align-items-center mb-2">
                                <span className="text-muted font-14 mr-2">{year}</span>
                                <hr className="DevelopmentTimelineForm__year-line" />
                            </div>
                            <div className="d-flex flex-column align-items-end">
                                {milestones.map(renderMilestoneRow)}
                            </div>
                        </div>
                    ))}

                    {milestonesWithoutDate.length > 0 && (
                        <div className="d-flex flex-column align-items-end">
                            {milestonesWithoutDate.map(renderMilestoneRow)}
                        </div>
                    )}
                </div>

                <div className="d-flex justify-content-end mt-3">
                    <NSButton
                        color="primary"
                        outline
                        className="px-4"
                        callback={handleAddMilestone}
                    >
                        Add milestone
                    </NSButton>
                </div>

                {shouldShowError('developmentTimeline.milestones') && (
                    <p className="text-danger small mt-1 mb-0">
                        {getFieldError('developmentTimeline.milestones')}
                    </p>
                )}

                <div className="text-muted font-12">Auto-filled from pro-forma</div>
            </NSCard>
        </div>
    );
};

export default DevelopmentTimelineForm;
